import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

const B2B_STATUSES = ['low', 'in_stock', 'out_of_stock', 'backorder'];
const QTY_PLACEHOLDER = '@@QTY@@';

/** @type {Map<string, Promise<any>>} fallback cache when the shared B2B store isn't present */
const variantInventoryCache = new Map();

/**
 * Fetches location-adjusted inventory for a variant from the B2B inventory worker.
 * Prefers the shared store populated by b2b-inventory-check.js so a product page
 * doesn't refetch what its batch prefetch already has.
 *
 * @param {string} workerUrl - Base URL of the inventory worker.
 * @param {string} variantId - The variant ID.
 * @returns {Promise<any>} The worker result for the variant.
 */
function fetchVariantInventory(workerUrl, variantId) {
  const shared = window.SlingshotB2BInventory;
  if (shared) return shared.get(variantId);

  if (!workerUrl) return Promise.reject(new Error('No inventory worker URL configured'));

  const key = String(variantId);
  let request = variantInventoryCache.get(key);

  if (!request) {
    request = fetch(`${workerUrl}/api/inventory-check?variant_id=${encodeURIComponent(key)}`)
      .then((response) => {
        if (!response.ok) throw new Error(`Inventory API error: ${response.status}`);
        return response.json();
      })
      .catch((error) => {
        variantInventoryCache.delete(key);
        throw error;
      });
    variantInventoryCache.set(key, request);
  }

  return request;
}

/**
 * Rewrites the rendered status to reflect inventory outside the excluded locations,
 * mirroring how the B2B order form reports quantity.
 *
 * @param {HTMLElement} statusEl - The `.product-inventory__status` element.
 * @param {any} result - The worker result for the current variant.
 */
function renderLocationAdjustedStatus(statusEl, result) {
  // Untracked variants have no per-location levels — leave the theme's answer alone.
  if (!result || result.reason === 'untracked') return;

  const quantity = Number(result.totalOtherLocations) || 0;
  const inStock = result.available === true && quantity > 0;
  const threshold = Number(statusEl.dataset.threshold) || 0;
  const showQuantity = statusEl.dataset.showQuantity === 'true';

  let status;
  let text;

  if (!inStock && result.backorder) {
    status = 'backorder';
    text = statusEl.dataset.labelBackorder;
  } else if (!inStock) {
    status = 'out_of_stock';
    text = statusEl.dataset.labelOut;
  } else if (quantity <= threshold) {
    status = 'low';
    if (!showQuantity) {
      text = statusEl.dataset.labelLow;
    } else if (quantity === 1 && statusEl.dataset.labelCountOne) {
      text = statusEl.dataset.labelCountOne;
    } else {
      const template = statusEl.dataset.labelCount || '';
      text = template.includes(QTY_PLACEHOLDER) ? template.replace(QTY_PLACEHOLDER, String(quantity)) : String(quantity);
    }
  } else {
    status = 'in_stock';
    text = statusEl.dataset.labelInStock;
  }

  const icon = statusEl.querySelector('.product-inventory__icon');
  if (icon) {
    for (const name of B2B_STATUSES) icon.classList.remove(`product-inventory__icon-${name}`);
    icon.classList.add(`product-inventory__icon-${status}`);
  }

  const textEl = statusEl.querySelector('.product-inventory__text');
  if (textEl && text) textEl.textContent = text;
}

class ProductInventory extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    this.adjustForExcludedLocations();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
  }

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
    this.adjustForExcludedLocations();
  };

  /**
   * For B2B customers, replaces Shopify's total with the quantity available
   * outside the excluded locations. No-op for everyone else.
   */
  adjustForExcludedLocations() {
    const statusEl = this.querySelector('.product-inventory__status[data-b2b-adjust]');
    if (!(statusEl instanceof HTMLElement)) return;

    const variantId = statusEl.dataset.variantId;
    if (!variantId) return;

    fetchVariantInventory(statusEl.dataset.workerUrl ?? '', variantId)
      .then((result) => {
        // A later variant change may have re-rendered the block while we waited.
        const current = this.querySelector('.product-inventory__status[data-b2b-adjust]');
        if (!(current instanceof HTMLElement) || current.dataset.variantId !== variantId) return;
        renderLocationAdjustedStatus(current, result);
      })
      .catch((error) => {
        // Fail open — Shopify's own numbers stay on screen.
        console.warn('[B2B Inventory] Inventory block adjustment failed:', error.message);
      });
  }
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
